import React, { useState } from 'react';
import { useRef, useEffect } from 'react';
import { useResizeObserver } from '../Truncate/use-resize-observer';
import CarouselComponent from './CarouselComponent';
import { reorder } from './utils';

const displacement = 24;

export type DynamicCarouselProps = {
  items: Array<any>
  itemCount: number
  showDragIcon?: boolean
  disableDragDrop?: boolean
  dragEndCallback?: Function
  addCallback?: Function
  deleteCallback?: Function
  type?: string,
  testId?: string
  version?: string
}



const DynamicCarousel = (props: DynamicCarouselProps) => {

  const {
    items = [],
    showDragIcon = false,
    disableDragDrop = false,
    testId = 'cs-carousel',
    version = 'v2',
  } = props;

  let carouselContainer = useRef(null);
  let carouselItemContainer = useRef(null);
  let outerContainer = useRef(null);
  const dimension = useResizeObserver(outerContainer);


  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [carouselItems, setCarouselItems] = useState(items || []);

  const CarouselMinOffset = 'Carousel--min-offset';
  const CarouselMaxOffset = 'Carousel--max-offset';

  const currButtonStatus = () => {
    const CarouselBlockItemList = carouselItemContainer?.current?.getElementsByClassName('Carousel__item');

    if (!CarouselBlockItemList || CarouselBlockItemList.length === 0)
      return;

    const left = parseInt(carouselItemContainer?.current?.style?.left) || 0;
    const lastElementRightX = CarouselBlockItemList && CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetLeft + CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetWidth;
    const carouselContainerRightX = outerContainer?.current?.offsetWidth || 0;

    if (carouselContainerRightX !== null && lastElementRightX !== null && typeof left === 'number') {
      // condition for having prevButton
      if (left < 0) {
        outerContainer.current.classList.add(CarouselMinOffset)
      } else {
        if (outerContainer.current.classList.contains(CarouselMinOffset)) {
          outerContainer.current.classList.remove(CarouselMinOffset)
        }
      }
      // condition for having nextButton
      if (carouselContainerRightX < lastElementRightX + left) {
        outerContainer.current.classList.add(CarouselMaxOffset)
      } else {
        if (outerContainer.current.classList.contains(CarouselMaxOffset)) {
          outerContainer.current.classList.remove(CarouselMaxOffset)
        }
      }
    }
  }

  const handleNext = () => {
    let CarouselBlockItemList = carouselItemContainer?.current?.getElementsByClassName('Carousel__item');
    if (!CarouselBlockItemList)
      return;
    const left = parseInt(carouselItemContainer.current.style.left) || 0;
    const outerWidth = outerContainer.current.offsetWidth;
    for (let i = 0; i != CarouselBlockItemList.length; i++) {
      const element = CarouselBlockItemList[i];
      const newLeft = element.offsetLeft + element.offsetWidth + left - outerWidth;
      if (newLeft > 0) {
        if (i == CarouselBlockItemList.length - 1) {
          carouselItemContainer.current.style.left = `${left - newLeft}px`;
          currButtonStatus();
          return;
        } else {
          carouselItemContainer.current.style.left = `${left - newLeft - displacement}px`;
          currButtonStatus();
          return;
        }
      }
    }
    currButtonStatus();
  }

  const handlePrev = () => {
    let CarouselBlockItemList = carouselItemContainer?.current?.getElementsByClassName('Carousel__item');
    if (!CarouselBlockItemList)
      return;
    let left = parseInt(carouselItemContainer.current.style.left) || 0;
    for (let i = CarouselBlockItemList.length - 1; i >= 0; i--) {
      const element = CarouselBlockItemList[i];
      const right = element.offsetLeft + left;
      if (right < 0) {
        if (i === 0) {
          carouselItemContainer.current.style.left = `${parseInt(carouselItemContainer.current.style.left) - right}px`;
        } else {
          carouselItemContainer.current.style.left = `${parseInt(carouselItemContainer.current.style.left) - right + displacement}px`;
        }
        currButtonStatus();
        return;
      }
    }
    currButtonStatus();
  }

  const onDragEnd = ({ destination, source }) => {
    if (!destination) {
      return
    } else if (destination.index !== source.index) {
      setCarouselItems(reorder([...carouselItems], source.index, destination.index))
      if (props.dragEndCallback) {
        props.dragEndCallback(source.index, destination.index)
      }
    }
  }


  if (isAdding) {
    setTimeout(() => {
      let tempWidth = 0;
      let totalItemWidth = 0;
      let CarouselBlockItemList = carouselItemContainer?.current?.getElementsByClassName('Carousel__item');
      if (!CarouselBlockItemList)
        return;

      tempWidth = CarouselBlockItemList && CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetWidth

      totalItemWidth = CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetWidth + CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetLeft;

      if (totalItemWidth > outerContainer.current.offsetWidth) {
        carouselItemContainer.current.style.left = `${outerContainer.current.offsetWidth - totalItemWidth}px`;
      }

      if (props.addCallback) props.addCallback();

      setIsAdding(false);

      currButtonStatus();
    });
  }


  if (isDeleting) {
    setTimeout(() => {
      let CarouselBlockItemList = carouselItemContainer?.current?.getElementsByClassName('Carousel__item');
      if (!CarouselBlockItemList) {
        setIsDeleting(false);
        return;
      }

      const left = parseInt(carouselItemContainer.current.style.left) || 0;
      const lastEle = CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetWidth + CarouselBlockItemList[CarouselBlockItemList.length - 1].offsetLeft + left;
      const outerWidth = outerContainer.current.offsetWidth;

      const firstEle = CarouselBlockItemList[0].offsetLeft + left;

      if (Math.abs(firstEle) < (outerWidth - lastEle)) {
        carouselItemContainer.current.style.left = `0px`;
      } else if (outerWidth > lastEle) {
        carouselItemContainer.current.style.left = `${left + outerWidth - lastEle}px`;
      }

      if (props.deleteCallback) props.deleteCallback();

      setIsDeleting(false);
      currButtonStatus();
    });
  }


  useEffect(() => {
    setCarouselItems(items)
    if (props.itemCount > carouselItems.length && carouselItems.length > 0) {
      setIsAdding(true)
    } else if (props.itemCount < carouselItems.length && carouselItems.length > 0) {
      setIsDeleting(true)
    }
  }, [items])

  useEffect(() => {
    currButtonStatus();
  }, [dimension])

  return (
    <CarouselComponent
      carouselContainer={carouselContainer}
      carouselItemContainer={carouselItemContainer}
      outerContainer={outerContainer}
      handlePrev={handlePrev}
      handleNext={handleNext}
      onDragEnd={onDragEnd}
      carouselItems={carouselItems}
      dynamicWidth={true}
      version={version}
      showDragIcon={showDragIcon}
      disableDragDrop={disableDragDrop}
      testId={testId}
      {...props}
    />
  )
}

export default DynamicCarousel;

